import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const usePlayerData = (playerId: string | undefined) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: player, isLoading: playerLoading } = useQuery({
    queryKey: ['player', playerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('players')
        .select(`
          *,
          profile:profiles(
            id,
            first_name,
            last_name,
            email
          ),
          teams(
            id,
            name,
            clubs(
              id,
              name,
              organizations(
                id,
                name
              )
            )
          )
        `)
        .eq('id', playerId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!playerId,
  });

  const updatePlayerMutation = useMutation({
    mutationFn: async (updates: Record<string, unknown>) => {
      const { error } = await supabase
        .from('players')
        .update(updates)
        .eq('id', playerId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Player updated successfully');
      queryClient.invalidateQueries({ queryKey: ['player', playerId] });
    },
    onError: (error) => {
      console.error('Error updating player:', error);
      toast.error('Failed to update player');
    },
  });

  const deletePlayerMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('players')
        .delete()
        .eq('id', playerId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Player deleted successfully');
      if (player?.teams?.id) {
        navigate(`/superadmin/team/${player.teams.id}`);
      } else {
        navigate('/superadmin');
      }
    },
    onError: (error) => {
      console.error('Error deleting player:', error);
      toast.error('Failed to delete player');
    },
  });

  return {
    player,
    playerLoading,
    updatePlayerMutation,
    deletePlayerMutation
  };
};